import { gql } from '@apollo/client';

import sanitizeExpressions from './sanitizeExpressions';

const buildAggregateCompoundMetricQuery = ({
  metrics,
  expressions,
  filterInput,
  dateScope,
  query,
  alias,
  groupBy,
  subGroupBy,
  trendByCalendarInterval,
  trendByFixedIntervalDays,
  trendByFiscalInterval,
  groupByDayOfWeek,
  groupByRanges,
  groupByInterval,
  groupByHour,
  groupByMonth,
  forecastPeriods,
}: {
  metrics: MetricInput[];
  expressions: Expression2[];
  filterInput: FilterInput[];
  dateScope: DateRangeInput;
  query: string;
  alias: string;
  groupBy?: V5GroupBy;
  subGroupBy?: V5GroupBy;
  trendByCalendarInterval?: FleetOps.Interval;
  trendByFixedIntervalDays?: number;
  trendByFiscalInterval?: FixedPeriods.FiscalType;
  groupByDayOfWeek?: boolean;
  groupByRanges?: V5RangeAggregation;
  groupByInterval?: V5Histogram;
  groupByHour?: boolean;
  groupByMonth?: boolean;
  // Predicted buckets after the last trend bucket (aggregateCompoundMetric
  // only; special-metric resolvers don't declare it either)
  forecastPeriods?: number;
}) => {
  // Only include `subGroupBy` (the terms x terms second dimension) in the query
  // when it is actually used. Special-metric resolvers (e.g. aggregateDriverMetrics)
  // don't declare this argument, so emitting it unconditionally makes their queries
  // fail GraphQL validation and stops all special metrics rendering. When omitted,
  // the query reverts to the pre-subGroupBy shape that every resolver accepts.
  const subGroupByVarDecl = subGroupBy ? '$subGroupBy: GroupBy' : '';
  const subGroupByArg = subGroupBy ? 'subGroupBy: $subGroupBy' : '';
  const hasForecast = forecastPeriods !== undefined;
  const forecastVarDecl = hasForecast ? '$forecastPeriods: Int' : '';
  const forecastArg = hasForecast ? 'forecastPeriods: $forecastPeriods' : '';

  // Safety net: never ship an expression that references a metric id missing
  // from `metrics` (it would raise "parse error: Expected EOF" server-side).
  const safeExpressions = sanitizeExpressions({ metrics, expressions });

  return {
    query: gql`
      query ${alias}(
        $metrics: [MetricInput]!
        $filters: [FilterInput]!
        $dateScope: [DateRangeInput!]!
        $expressions: [Expression2]!
        $groupBy: GroupBy
        ${subGroupByVarDecl}
        $trendByCalendarInterval: Interval
        $trendByFiscalInterval: FiscalInterval
        $trendByFixedIntervalDays: Int
        $groupByDayOfWeek: Boolean
        $groupByRanges: RangeAggregation
        $groupByInterval: Histogram
        $groupByHour: Boolean
        $groupByMonth: Boolean
        ${forecastVarDecl}
      ) {
        ${query}(
          metrics: $metrics
          filters: $filters
          dateScope: $dateScope
          expressions: $expressions
          groupBy: $groupBy
          ${subGroupByArg}
          trendByCalendarInterval: $trendByCalendarInterval
          trendByFiscalInterval: $trendByFiscalInterval
          trendByFixedIntervalDays: $trendByFixedIntervalDays
          groupByDayOfWeek: $groupByDayOfWeek
          groupByRanges: $groupByRanges
          groupByInterval: $groupByInterval
          groupByHour: $groupByHour
          groupByMonth: $groupByMonth
          ${forecastArg}
        )
      }
    `,
    variables: {
      metrics,
      filters: filterInput,
      dateScope,
      expressions: safeExpressions,
      groupBy,
      ...(subGroupBy ? { subGroupBy } : {}),
      trendByCalendarInterval,
      trendByFixedIntervalDays,
      trendByFiscalInterval,
      groupByDayOfWeek,
      groupByRanges,
      groupByInterval,
      groupByHour,
      groupByMonth,
      ...(hasForecast ? { forecastPeriods } : {}),
    },
  };
};

export default buildAggregateCompoundMetricQuery;
